import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import JourneyModal from './JourneyModal';
import { journeyService } from '../services/journeyService';

const Journeys = () => {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingJourney, setEditingJourney] = useState(null);
  const [prefillJourney, setPrefillJourney] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const fetchJourneys = useCallback(async () => {
    try {
      setLoading(true);
      const data = await journeyService.getJourneys();
      setJourneys(data.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load journeys. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJourneys();
  }, [fetchJourneys]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleAddJourney = async (formData) => {
    await journeyService.createJourney(formData);
    await fetchJourneys();
    setShowModal(false);
    showSuccess('Journey added successfully!');
  };

  const handleEditJourney = async (formData) => {
    await journeyService.updateJourney(editingJourney.id, formData);
    await fetchJourneys();
    setEditingJourney(null);
    setShowModal(false);
    showSuccess('Journey updated successfully!');
  };

  const handleDeleteJourney = async (id) => {
    try {
      await journeyService.deleteJourney(id);
      await fetchJourneys();
      setDeleteConfirm(null);
      showSuccess('Journey deleted successfully!');
    } catch (err) {
      setError('Failed to delete journey. Please try again.');
    }
  };

  const openEditModal = (journey) => {
    setEditingJourney(journey);
    setPrefillJourney(null);
    setShowModal(true);
  };

  const openReturnModal = (journey) => {
    const base = journey.started_at ? new Date(journey.started_at) : new Date();
    base.setSeconds(0, 0);
    base.setHours(base.getHours() + 8);
    const returnStartedAt = base.toISOString().slice(0, 16);

    setEditingJourney(null);
    setPrefillJourney({
      job_id: journey.job_id,
      journey_reason_name: 'Return to office',
      start_postcode: journey.end_postcode,
      end_postcode: journey.start_postcode,
      mileage: journey.mileage,
      started_at: returnStartedAt,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingJourney(null);
    setPrefillJourney(null);
  };
  const googleMapsUrl = (start, end) =>
    `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(start)}&destination=${encodeURIComponent(end)}`;

  const sortedJourneys = [...journeys].sort((a, b) => {
    const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
    const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
    return sortDir === 'asc' ? aTime - bTime : bTime - aTime;
  });

  const totalMileage = journeys.reduce((sum, j) => sum + parseFloat(j.mileage || 0), 0);

  const exportToCSV = () => {
    const headers = ['Date/Time', 'Site', 'Job', 'Reason', 'Start Postcode', 'End Postcode', 'Mileage'];
    const rows = sortedJourneys.map((j) => [
      j.started_at
        ? new Date(j.started_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : '',
      j.job?.site?.name || '',
      j.job?.title || '',
      j.reason?.name || '',
      j.start_postcode || '',
      j.end_postcode || '',
      parseFloat(j.mileage || 0).toFixed(1),
    ]);

    const escape = (val) => `"${String(val).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((row) => row.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'journeys.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-2.5 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <span className="text-gray-900 dark:text-white font-medium">Journeys</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <ThemeToggle />
              <span className="text-gray-600 dark:text-gray-300 font-medium text-sm sm:text-base order-last sm:order-none">
                {user?.name}
              </span>
              <button
                onClick={logout}
                className="bg-red-500 text-white border-0 rounded-md px-3 sm:px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-2.5 py-8">

        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-green-700 dark:text-green-300 text-sm font-medium">
            ✓ {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Journey List</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              {journeys.length} {journeys.length === 1 ? 'journey' : 'journeys'} total
              {journeys.length > 0 && (
                <span className="ml-2">· {totalMileage.toFixed(1)} miles total</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {journeys.length > 0 && (
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
              >
                ↓ Export CSV
              </button>
            )}
            <button
              onClick={() => { setEditingJourney(null); setShowModal(true); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity shadow-md"
            >
              <span className="text-lg leading-none">+</span>
              Add Journey
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
                Loading journeys...
              </div>
            </div>
          ) : journeys.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="text-5xl mb-4">🗺️</div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No journeys yet</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">Add your first journey to get started.</p>
              <button
                onClick={() => { setEditingJourney(null); setShowModal(true); }}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                <span className="text-lg leading-none">+</span>
                Add Journey
              </button>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full whitespace-nowrap">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                      <th
                        className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                        onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                      >
                        Date / Time {sortDir === 'asc' ? '↑' : '↓'}
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Site</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Job</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reason</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Start Post Code</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">End Post Code</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Mileage</th>
                      <th className="sticky right-0 text-right px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.2)]" style={{ backgroundColor: '#2b3544' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedJourneys.map((journey) => (
                      <tr key={journey.id} className="text-sm hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                          {journey.started_at
                            ? new Date(journey.started_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                            : '—'}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                          {journey.job?.site
                            ? <Link to={`/sites/${journey.job.site.id}`} className="text-blue-600 dark:text-blue-400 hover:underline">{journey.job.site.name}</Link>
                            : '—'}
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                          {journey.job
                            ? <button onClick={() => navigate(`/jobs/${journey.job_id}`)} className="text-blue-600 dark:text-blue-400 hover:underline font-medium text-left">{journey.job.title}</button>
                            : '—'}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{journey.reason?.name || '—'}</td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(journey.start_postcode)}`} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600 dark:text-blue-400">{journey.start_postcode}</a>
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(journey.end_postcode)}`} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600 dark:text-blue-400">{journey.end_postcode}</a>
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{parseFloat(journey.mileage).toFixed(1)} mi</td>
                        <td className="sticky right-0 bg-white dark:bg-gray-800 px-6 py-4 text-right shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.2)]">
                          <div className="flex justify-end gap-2">
                            <a
                              href={googleMapsUrl(journey.start_postcode, journey.end_postcode)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1.5 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors font-medium"
                            >
                              Maps
                            </a>
                            <button
                              onClick={() => openReturnModal(journey)}
                              className="px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-md transition-colors font-medium"
                            >
                              Return
                            </button>
                            <button
                              onClick={() => openEditModal(journey)}
                              className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(journey)}
                              className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {journeys.map((journey) => (
                  <div key={journey.id} className="p-5 space-y-1">
                    <div className="flex justify-between items-start">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {journey.job
                          ? <button onClick={() => navigate(`/jobs/${journey.job_id}`)} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold text-left">{journey.job.title}</button>
                          : '—'}
                      </h3>
                      {journey.job?.site && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          <Link to={`/sites/${journey.job.site.id}`} className="text-blue-500 dark:text-blue-400 hover:underline">{journey.job.site.name}</Link>
                        </p>
                      )}
                      <div className="flex gap-2 ml-2">
                        <a
                          href={googleMapsUrl(journey.start_postcode, journey.end_postcode)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-green-600 dark:text-green-400 font-medium"
                        >
                          Maps
                        </a>
                        <button
                          onClick={() => openReturnModal(journey)}
                          className="text-sm text-purple-600 dark:text-purple-400 font-medium"
                        >
                          Return
                        </button>
                        <button
                          onClick={() => openEditModal(journey)}
                          className="text-sm text-blue-600 dark:text-blue-400 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(journey)}
                          className="text-sm text-red-600 dark:text-red-400 font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-blue-600 dark:text-blue-400">{journey.reason?.name || '—'}</p>
                    {journey.started_at && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(journey.started_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(journey.start_postcode)}`} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600 dark:text-blue-400">{journey.start_postcode}</a>
                      {' → '}
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(journey.end_postcode)}`} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600 dark:text-blue-400">{journey.end_postcode}</a>
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{parseFloat(journey.mileage).toFixed(1)} miles</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {showModal && (
        <JourneyModal
          journey={editingJourney}
          prefill={prefillJourney}
          onSave={editingJourney ? handleEditJourney : handleAddJourney}
          onClose={closeModal}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Journey</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete this journey from <strong>{deleteConfirm.start_postcode}</strong> to <strong>{deleteConfirm.end_postcode}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteJourney(deleteConfirm.id)}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Journeys;
